import { useMemo, useState } from 'react';
import { Lock, Store as StoreIcon, Unlock } from 'lucide-react';

import type { AdminStore } from '@/client/types/stores';
import { PERMISSIONS } from '@/client/config/permissions';
import { PermissionButton } from '@/client/components/admin/PermissionButton';
import { useGate } from '@/client/hooks/useGate';
import {
  useSetStoreSlug,
  useUnlockStoreSlug,
  useUpdateStoreProfile,
  type StoreProfileUpdate,
} from '@/client/api/stores/stores.mutation';
import { useClearStoreImage, useUploadStoreImage } from '@/client/api/stores/parity.mutation';
import { Input } from '@/components/ui/input';

import { storeBannerUrl, storeLogoUrl } from '../../storeMedia';
import { SectionCard } from '../shared';
import { ReasonDialog } from '../ReasonDialog';
import { FieldRow, SaveRow } from './controls';

type ProfileForm = Required<Pick<StoreProfileUpdate, 'name'>> & {
  tagline: string;
  description_short: string;
  description: string;
  country_iso2: string;
  locale: string;
};

const toForm = (s: AdminStore): ProfileForm => ({
  name: s.name ?? '',
  tagline: s.tagline ?? '',
  description_short: s.description_short ?? '',
  description: s.description ?? '',
  country_iso2: s.country_iso2 ?? '',
  locale: s.locale ?? '',
});

const textareaCls =
  'w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60';

const ProfileCard: React.FC<{ store: AdminStore }> = ({ store }) => {
  const initial = useMemo(() => toForm(store), [store]);
  const [form, setForm] = useState<ProfileForm>(initial);
  const [confirm, setConfirm] = useState(false);
  const update = useUpdateStoreProfile();
  const canEdit = useGate(PERMISSIONS.store.profile).allowed;

  const changes = (Object.keys(form) as (keyof ProfileForm)[]).reduce<StoreProfileUpdate>((acc, k) => {
    if (form[k] !== initial[k]) {
      const v = form[k].trim();
      (acc as Record<string, unknown>)[k] = k === 'name' ? v : v || null;
    }
    return acc;
  }, {});
  const dirty = Object.keys(changes).length > 0;
  const set = (k: keyof ProfileForm, v: string) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <SectionCard title="Profile" description="What buyers see on the storefront. Changes are logged and the seller can see them.">
      <div className="divide-y divide-gray-100">
        <FieldRow label="Store name" hint="Renaming may regenerate the handle unless it is locked.">
          <Input value={form.name} disabled={!canEdit} onChange={(e) => set('name', e.target.value)} className="w-72" />
        </FieldRow>
        <FieldRow label="Tagline">
          <Input value={form.tagline} disabled={!canEdit} onChange={(e) => set('tagline', e.target.value)} className="w-72" />
        </FieldRow>
        <FieldRow label="Country / locale" hint="ISO-2 country and locale code.">
          <div className="flex gap-2">
            <Input value={form.country_iso2} disabled={!canEdit} maxLength={2} onChange={(e) => set('country_iso2', e.target.value.toUpperCase())} className="w-16" placeholder="GH" />
            <Input value={form.locale} disabled={!canEdit} onChange={(e) => set('locale', e.target.value)} className="w-24" placeholder="en-GH" />
          </div>
        </FieldRow>
      </div>
      <div className="mt-3 space-y-3">
        <label className="block space-y-1 text-sm">
          <span className="font-medium text-gray-900">Short description</span>
          <textarea rows={2} value={form.description_short} disabled={!canEdit} onChange={(e) => set('description_short', e.target.value)} className={textareaCls} />
        </label>
        <label className="block space-y-1 text-sm">
          <span className="font-medium text-gray-900">About</span>
          <textarea rows={5} value={form.description} disabled={!canEdit} onChange={(e) => set('description', e.target.value)} className={textareaCls} />
        </label>
      </div>
      <SaveRow
        dirty={dirty && form.name.trim().length > 0}
        saving={update.isPending}
        onSave={() => setConfirm(true)}
        onReset={() => setForm(initial)}
      />
      {confirm && (
        <ReasonDialog
          open
          title="Save store profile"
          description={`Fields: ${Object.keys(changes).join(', ')}.`}
          confirmLabel="Save"
          reasonRequired
          loading={update.isPending}
          onConfirm={(reason) =>
            update.mutate({ storeid: store.storeid, ...changes, reason }, { onSuccess: () => setConfirm(false) })
          }
          onClose={() => setConfirm(false)}
        />
      )}
    </SectionCard>
  );
};

const HandleCard: React.FC<{ store: AdminStore }> = ({ store }) => {
  const current = (store.slug ?? '').replace(/^\/?store\//, '').replace(/^\//, '');
  const [handle, setHandle] = useState(current);
  const [dialog, setDialog] = useState<null | 'set' | 'unlock'>(null);
  const setSlug = useSetStoreSlug();
  const unlock = useUnlockStoreSlug();
  const locked = store.slug_locked === 1;
  const changed = handle.trim() !== '' && handle.trim() !== current;

  return (
    <SectionCard
      title="Storefront handle"
      description="Setting a handle here locks it, so a seller rename won't regenerate it."
      actions={
        <span className="inline-flex items-center gap-1 text-xs text-gray-500">
          {locked ? <Lock className="h-3 w-3" /> : <Unlock className="h-3 w-3" />}
          {locked ? 'Locked' : 'Follows the name'}
        </span>
      }
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm text-gray-400">/store/</span>
        <Input value={handle} onChange={(e) => setHandle(e.target.value.toLowerCase())} className="w-64" />
        <PermissionButton code={PERMISSIONS.store.profile} disabled={!changed} onClick={() => setDialog('set')}>
          Set &amp; lock
        </PermissionButton>
        {locked && (
          <PermissionButton code={PERMISSIONS.store.profile} variant="outline" onClick={() => setDialog('unlock')}>
            Unlock
          </PermissionButton>
        )}
      </div>
      {dialog && (
        <ReasonDialog
          open
          title={dialog === 'set' ? `Set handle to /store/${handle.trim()}` : 'Unlock handle'}
          description={
            dialog === 'set'
              ? 'The old handle is kept in history so existing links still resolve.'
              : "The current handle stays; the seller's next rename regenerates it."
          }
          confirmLabel={dialog === 'set' ? 'Set handle' : 'Unlock'}
          reasonRequired={dialog === 'set'}
          loading={setSlug.isPending || unlock.isPending}
          onConfirm={(reason) =>
            dialog === 'set'
              ? setSlug.mutate({ storeid: store.storeid, slug: handle.trim(), reason }, { onSuccess: () => setDialog(null) })
              : unlock.mutate({ storeid: store.storeid, reason }, { onSuccess: () => setDialog(null) })
          }
          onClose={() => setDialog(null)}
        />
      )}
    </SectionCard>
  );
};

/** Replace or remove the logo (`photo`) or banner. PNG/JPG, one file. */
const MediaControls: React.FC<{ store: AdminStore; kind: 'photo' | 'banner'; hasImage: boolean }> = ({ store, kind, hasImage }) => {
  const [file, setFile] = useState<File | null>(null);
  const [removing, setRemoving] = useState(false);
  const upload = useUploadStoreImage();
  const clear = useClearStoreImage();
  const noun = kind === 'photo' ? 'logo' : 'banner';

  return (
    <div className="flex items-center gap-2">
      <label className="cursor-pointer rounded-md border border-gray-200 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50">
        {hasImage ? `Replace ${noun}` : `Upload ${noun}`}
        <input
          type="file"
          accept="image/png,image/jpeg"
          className="hidden"
          onChange={(e) => { setFile(e.target.files?.[0] ?? null); e.target.value = ''; }}
        />
      </label>
      {hasImage && (
        <PermissionButton code={PERMISSIONS.store.profile} size="sm" variant="ghost" className="h-7 text-xs text-red-600" onClick={() => setRemoving(true)}>
          Remove
        </PermissionButton>
      )}
      {file && (
        <ReasonDialog
          open
          title={`Upload new ${noun}`}
          description={`${file.name} (${Math.round(file.size / 1024)} KB). Replaces the current ${noun} on the storefront.`}
          confirmLabel="Upload"
          reasonRequired
          loading={upload.isPending}
          onConfirm={(reason) => upload.mutate({ storeid: store.storeid, kind, file, reason }, { onSuccess: () => setFile(null) })}
          onClose={() => setFile(null)}
        />
      )}
      {removing && (
        <ReasonDialog
          open
          title={`Remove ${noun}`}
          description="The storefront falls back to the default placeholder."
          confirmLabel="Remove"
          destructive
          reasonRequired
          loading={clear.isPending}
          onConfirm={(reason) => clear.mutate({ storeid: store.storeid, kind, reason }, { onSuccess: () => setRemoving(false) })}
          onClose={() => setRemoving(false)}
        />
      )}
    </div>
  );
};

export const BrandSection: React.FC<{ store: AdminStore }> = ({ store }) => {
  const banner = storeBannerUrl(store);
  const logo = storeLogoUrl(store);

  return (
    <div className="space-y-4">
      <SectionCard
        title="Brand media"
        description="Logo and banner shown on the storefront."
        actions={
          <div className="flex flex-col items-end gap-1">
            <MediaControls store={store} kind="photo" hasImage={!!logo} />
            <MediaControls store={store} kind="banner" hasImage={!!banner} />
          </div>
        }
      >
        <div className="relative mb-2 h-32 overflow-hidden rounded-lg bg-gradient-to-br from-gray-100 to-gray-200">
          {banner ? (
            <img src={banner} alt="" className="h-full w-full object-cover" />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center opacity-10">
              <StoreIcon className="h-20 w-20 text-gray-600" />
            </div>
          )}
          <div className="absolute -bottom-5 left-5 flex h-14 w-14 items-center justify-center overflow-hidden rounded-xl border-2 border-white bg-white shadow-md">
            {logo ? (
              <img src={logo} alt={store.name} className="h-full w-full object-cover" />
            ) : (
              <StoreIcon className="h-7 w-7 text-gray-400" />
            )}
          </div>
        </div>
        <div className="h-4" />
      </SectionCard>

      {/* Remount on each server change so the forms re-seed from fresh data. */}
      <ProfileCard key={String(store.date_modified ?? '')} store={store} />
      <HandleCard key={`slug-${store.slug ?? ''}`} store={store} />
    </div>
  );
};
